import React, { useState, useEffect, useCallback } from 'react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { cantieri } from '../../utils/api';
import ReportGiornalieroPDF, { ReportCompletoGiornalieroPDF } from '../ReportGiornalieroPDF';

const slugify = s => s.toLowerCase()
  .replace(/[àáâãäå]/g,'a').replace(/[èéêë]/g,'e')
  .replace(/[ìíîï]/g,'i').replace(/[òóôõö]/g,'o')
  .replace(/[ùúûü]/g,'u').replace(/[^a-z0-9]+/g,'-')
  .replace(/^-|-$/g,'');

const fmtDate = (d) => {
  const dt = new Date(d);
  const now = new Date();
  const isToday = dt.toDateString() === now.toDateString();
  const isYesterday = dt.toDateString() === new Date(Date.now() - 86400000).toDateString();
  if (isToday) return 'Oggi — ' + dt.toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });
  if (isYesterday) return 'Ieri — ' + dt.toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });
  return dt.toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });
};

export default function ReportTab({ cantiereId, dashData }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [viewingId, setViewingId] = useState(null);

  const fetch = useCallback(() => {
    setLoading(true);
    cantieri.reports(cantiereId)
      .then(res => setReports(res.data?.results || res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [cantiereId]);

  useEffect(() => { fetch(); }, [fetch]);

  const handleCreate = async (data) => {
    try {
      const { _voci_costo, ...reportData } = data;
      await cantieri.createReport(cantiereId, reportData);
      // Crea EventoCantiere tipo=costo per ogni voce
      if (_voci_costo && _voci_costo.length > 0) {
        for (const voce of _voci_costo) {
          await cantieri.addEvento(cantiereId, {
            tipo: 'costo',
            contenuto: voce.descrizione,
            importo: parseFloat(voce.importo),
            data_riferimento: reportData.data,
            metadata: { fonte: 'report_giornaliero', categoria: voce.categoria || '' },
          });
        }
      }
      setShowForm(false);
      fetch();
    } catch {}
  };

  const handleUpdate = async (data) => {
    try {
      await cantieri.updateReport(cantiereId, editingId, data);
      setEditingId(null);
      fetch();
    } catch {}
  };

  const handleDelete = async (reportId) => {
    if (!window.confirm('Eliminare questo report?')) return;
    try { await cantieri.deleteReport(cantiereId, reportId); fetch(); } catch {}
  };

  if (loading) return <div style={{ padding: 20, color: 'var(--ds-muted)' }}>Caricamento report...</div>;

  const visible = showAll ? reports : reports.slice(0, 5);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontSize: 16, fontWeight: 500 }}>Report Giornaliero</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {reports.length > 0 && (
            <>
              <PDFDownloadLink
                document={<ReportCompletoGiornalieroPDF reports={reports} cantiere={dashData?.cantiere} azienda={dashData?.azienda} versione="impresa" />}
                fileName={'report-completo-' + slugify(dashData?.cantiere?.titolo || 'cantiere') + '.pdf'}
              >
                {({ loading }) => <button style={btnSmall} disabled={loading}>{loading ? '...' : '📄 PDF Impresa'}</button>}
              </PDFDownloadLink>
              <PDFDownloadLink
                document={<ReportCompletoGiornalieroPDF reports={reports} cantiere={dashData?.cantiere} azienda={dashData?.azienda} versione="cliente" />}
                fileName={'report-completo-cliente-' + slugify(dashData?.cantiere?.titolo || 'cantiere') + '.pdf'}
              >
                {({ loading }) => <button style={btnSmall} disabled={loading}>{loading ? '...' : '📄 PDF Cliente'}</button>}
              </PDFDownloadLink>
            </>
          )}
          <button onClick={() => setShowForm(true)} style={btnPrimary}>+ Nuovo report</button>
        </div>
      </div>

      {showForm && <ReportForm onSubmit={handleCreate} onCancel={() => setShowForm(false)} />}

      {reports.length === 0 && !showForm ? (
        <EmptyState icon="📊" title="Nessun report" subtitle="Crea il primo report giornaliero per questo cantiere." />
      ) : (
        <>
          {visible.map(r => (
            editingId === r.id
              ? <ReportForm key={r.id} initialData={r} onSubmit={handleUpdate} onCancel={() => setEditingId(null)} isEditing />
              : <ReportCard key={r.id} report={r} onDelete={() => handleDelete(r.id)} onEdit={() => setEditingId(r.id)} onView={() => setViewingId(r.id)} dashData={dashData} />
          ))}
          {reports.length > 5 && !showAll && (
            <div style={{ textAlign: 'center', marginTop: 12 }}>
              <button onClick={() => setShowAll(true)} style={{ ...linkBtn, fontSize: 13 }}>
                📊 Mostra report precedenti
              </button>
            </div>
          )}
        </>
      )}

      {viewingId && (
        <ReportView
          report={reports.find(x => x.id === viewingId)}
          onClose={() => setViewingId(null)}
        />
      )}
    </div>
  );
}

function ReportCard({ report: r, onDelete, onEdit, onView, dashData }) {
  const isVocale = r.creato_via === 'voce';
  return (
    <div style={{
      padding: '16px 20px', marginBottom: 12, borderRadius: 'var(--radius-card)',
      border: '1px solid var(--ds-border)', background: 'var(--chip)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
        <div style={{ fontSize: 12, color: 'var(--ds-muted)' }}>{fmtDate(r.data)}</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {isVocale && (
            <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 'var(--radius-card)', background: 'rgba(46,160,132,0.12)', color: 'var(--brand)' }}>
              🎙️ Generato da vocale
            </span>
          )}
          <button onClick={onView} style={btnSmall}>Vedi</button>
          <button onClick={onEdit} style={btnSmall}>Modifica</button>
          <button onClick={onDelete} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--ds-muted)', fontSize: 14 }}>×</button>
        </div>
      </div>

      <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--ds-text)', marginBottom: 6 }}>
        Report Giornaliero{r.fase_corrente ? ' — ' + r.fase_corrente : ''}
      </div>

      {r.trascrizione_vocale && (
        <div style={{ fontSize: 13, color: 'var(--ds-muted)', fontStyle: 'italic', marginBottom: 10, lineHeight: 1.5 }}>
          "{r.trascrizione_vocale.slice(0, 200)}{r.trascrizione_vocale.length > 200 ? '...' : ''}"
        </div>
      )}
      {!r.trascrizione_vocale && r.attivita_svolte && (
        <div style={{ fontSize: 13, color: 'var(--ds-muted)', marginBottom: 10, lineHeight: 1.5 }}>
          {r.attivita_svolte.slice(0, 200)}{r.attivita_svolte.length > 200 ? '...' : ''}
        </div>
      )}

      {/* Barra SAL */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 10, color: 'var(--ds-muted)', textTransform: 'uppercase', marginBottom: 4 }}>Avanzamento SAL</div>
        <div style={{ height: 8, borderRadius: 4, background: 'var(--ds-border)' }}>
          <div style={{ height: '100%', borderRadius: 4, background: 'var(--brand)', width: r.percentuale_avanzamento_globale + '%', transition: 'width 0.3s' }} />
        </div>
        <div style={{ fontSize: 11, color: 'var(--ds-muted)', marginTop: 2 }}>{r.percentuale_avanzamento_globale}% completato</div>
      </div>

      {/* Badge info + PDF */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {r.numero_operai > 0 && <Badge text={r.numero_operai + ' operai'} />}
          {r.ore_totali > 0 && <Badge text={r.ore_totali + ' ore'} />}
          {r.meteo && <Badge text={r.meteo} />}
          {(r.presenze || []).length > 0 && (
            <span style={{ fontSize: 11, color: 'var(--ds-muted)', alignSelf: 'center' }}>
              👷 {r.presenze.map(p => `${p.nome} ${p.ore}h`).join(', ')}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <PDFDownloadLink
            document={<ReportGiornalieroPDF report={r} cantiere={dashData?.cantiere} azienda={dashData?.azienda} versione="impresa" />}
            fileName={'report-' + r.data + '-impresa.pdf'}
          >
            {({ loading }) => <button style={btnSmall} disabled={loading}>{loading ? '...' : 'PDF Impresa'}</button>}
          </PDFDownloadLink>
          <PDFDownloadLink
            document={<ReportGiornalieroPDF report={r} cantiere={dashData?.cantiere} azienda={dashData?.azienda} versione="cliente" />}
            fileName={'report-' + r.data + '-cliente.pdf'}
          >
            {({ loading }) => <button style={btnSmall} disabled={loading}>{loading ? '...' : 'PDF Cliente'}</button>}
          </PDFDownloadLink>
        </div>
      </div>
    </div>
  );
}

function Badge({ text }) {
  return (
    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: 'var(--chip)', color: 'var(--ds-muted)' }}>
      {text}
    </span>
  );
}

const METEO_VIEW = { sole: 'Sole', nuvoloso: 'Nuvoloso', pioggia: 'Pioggia', freddo: 'Freddo', vento: 'Vento' };

function ReportView({ report: r, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!r) return null;

  const sezione = (titolo, contenuto) => (contenuto ? (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)', textTransform: 'uppercase', marginBottom: 4 }}>{titolo}</div>
      <div style={{ fontSize: 14, color: 'var(--ds-text)', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{contenuto}</div>
    </div>
  ) : null);

  return (
    <div onClick={onClose} style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.5)', zIndex: 2200,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--chip)', borderRadius: 'var(--radius-card)', width: '100%', maxWidth: 560,
        maxHeight: '90vh', overflowY: 'auto', padding: 24,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 16, fontWeight: 500 }}>
            Report del {fmtDate(r.data)}{r.fase_corrente ? ' — ' + r.fase_corrente : ''}
          </div>
          <button onClick={onClose} aria-label="Chiudi" style={{
            width: 44, height: 44, border: 'none', background: 'none', cursor: 'pointer',
            fontSize: 22, color: 'var(--ds-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>×</button>
        </div>

        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: 'var(--ds-muted)', textTransform: 'uppercase', marginBottom: 4 }}>Avanzamento SAL</div>
          <div style={{ height: 8, borderRadius: 4, background: 'var(--ds-border)' }}>
            <div style={{ height: '100%', borderRadius: 4, background: 'var(--brand)', width: (r.percentuale_avanzamento_globale || 0) + '%' }} />
          </div>
          <div style={{ fontSize: 11, color: 'var(--ds-muted)', marginTop: 2 }}>{r.percentuale_avanzamento_globale || 0}% completato</div>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
          {r.numero_operai > 0 && <Badge text={r.numero_operai + ' operai'} />}
          {r.ore_totali > 0 && <Badge text={r.ore_totali + ' ore'} />}
          {r.meteo && <Badge text={METEO_VIEW[r.meteo] || r.meteo} />}
          {r.creato_via === 'voce' && <Badge text="🎙️ Da vocale" />}
          {(r.presenze || []).length > 0 && (
            <span style={{ fontSize: 12, color: 'var(--ds-muted)', alignSelf: 'center' }}>
              👷 {r.presenze.map(p => `${p.nome} ${p.ore}h`).join(', ')}
            </span>
          )}
        </div>

        {sezione('Attività svolte', r.attivita_svolte)}
        {sezione('Note per domani', r.note_per_domani)}
        {sezione('Note interne', r.note_interne)}
        {r.creato_via === 'voce' && sezione('Trascrizione vocale', r.trascrizione_vocale)}

        {!r.attivita_svolte && !r.note_per_domani && !r.note_interne && (
          <div style={{ fontSize: 13, color: 'var(--ds-muted)' }}>Nessun dettaglio testuale in questo report.</div>
        )}
      </div>
    </div>
  );
}

function ReportForm({ onSubmit, onCancel, initialData, isEditing }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    data: initialData?.data || today,
    meteo: initialData?.meteo || '',
    fase_corrente: initialData?.fase_corrente || '',
    percentuale_avanzamento_globale: initialData?.percentuale_avanzamento_globale ?? 0,
    attivita_svolte: initialData?.attivita_svolte || '',
    numero_operai: initialData?.numero_operai ?? 1,
    ore_totali: initialData?.ore_totali ?? 8,
    note_per_domani: initialData?.note_per_domani || '',
    note_interne: initialData?.note_interne || '',
  });
  const [vociCosto, setVociCosto] = useState([]);
  // VOX-ORE-C — split ore per persona (nome stringa, no anagrafica).
  const [presenze, setPresenze] = useState(
    (initialData?.presenze || []).map(p => ({ nome: p.nome, ore: p.ore }))
  );
  const METEO = { sole: '☀️', nuvoloso: '☁️', pioggia: '🌧️', freddo: '❄️', vento: '💨' };
  const CATEGORIE = ['Materiali', 'Attrezzatura', 'Manodopera', 'Trasporto', 'Smaltimento', 'Altro'];

  const addVoce = () => setVociCosto([...vociCosto, { descrizione: '', importo: '', categoria: '' }]);
  const removeVoce = (i) => setVociCosto(vociCosto.filter((_, idx) => idx !== i));
  const updateVoce = (i, field, value) => {
    const next = [...vociCosto];
    next[i] = { ...next[i], [field]: value };
    setVociCosto(next);
  };

  const addPresenza = () => setPresenze([...presenze, { nome: '', ore: form.ore_totali || 0 }]);
  const removePresenza = (i) => setPresenze(presenze.filter((_, idx) => idx !== i));
  const updatePresenza = (i, field, value) => {
    const next = [...presenze];
    next[i] = { ...next[i], [field]: value };
    setPresenze(next);
  };

  const handleSubmit = () => {
    const presenzeValide = presenze
      .filter(p => (p.nome || '').trim())
      .map(p => ({ nome: p.nome.trim(), ore: parseInt(p.ore) || 0 }));
    if (isEditing) {
      onSubmit({ ...form, presenze: presenzeValide });
    } else {
      const vociValide = vociCosto.filter(v => v.descrizione && v.importo);
      onSubmit({ ...form, _voci_costo: vociValide, presenze: presenzeValide });
    }
  };

  return (
    <div style={{ padding: 16, border: '1px solid var(--ds-border)', borderRadius: 'var(--radius-base)', marginBottom: 16, background: 'var(--chip)' }}>
      <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 12 }}>{isEditing ? 'Modifica report' : 'Nuovo report'}</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div><label style={labelStyle}>Data</label><input type="date" value={form.data} onChange={e => setForm({ ...form, data: e.target.value })} style={inputStyle} /></div>
        <div><label style={labelStyle}>Fase corrente</label><input placeholder="es. Demolizioni" value={form.fase_corrente} onChange={e => setForm({ ...form, fase_corrente: e.target.value })} style={inputStyle} /></div>
        <div>
          <label style={labelStyle}>Meteo</label>
          <div style={{ display: 'flex', gap: 4 }}>
            {Object.entries(METEO).map(([k, v]) => (
              <button key={k} onClick={() => setForm({ ...form, meteo: k })} style={{ padding: '6px 10px', borderRadius: 6, border: form.meteo === k ? '2px solid var(--brand)' : '1px solid var(--ds-border)', background: 'var(--ds-bg)', cursor: 'pointer', fontSize: 16 }}>{v}</button>
            ))}
          </div>
        </div>
        <div><label style={labelStyle}>Avanzamento %</label><input type="number" min={0} max={100} value={form.percentuale_avanzamento_globale} onChange={e => setForm({ ...form, percentuale_avanzamento_globale: parseInt(e.target.value) || 0 })} style={inputStyle} /></div>
        <div><label style={labelStyle}>Operai</label><input type="number" min={0} value={form.numero_operai} onChange={e => setForm({ ...form, numero_operai: parseInt(e.target.value) || 0 })} style={inputStyle} /></div>
        <div><label style={labelStyle}>Ore</label><input type="number" min={0} value={form.ore_totali} onChange={e => setForm({ ...form, ore_totali: parseInt(e.target.value) || 0 })} style={inputStyle} /></div>
        <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>Attività svolte</label><textarea rows={3} placeholder="Descrivi le attività..." value={form.attivita_svolte} onChange={e => setForm({ ...form, attivita_svolte: e.target.value })} style={{ ...inputStyle, resize: 'vertical' }} /></div>
      </div>

      {/* Voci di costo — solo in creazione */}
      {!isEditing && <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--ds-border)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <label style={labelStyle}>💸 Voci di costo</label>
          <button onClick={addVoce} style={btnSmall}>+ Aggiungi costo</button>
        </div>
        {vociCosto.length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--ds-muted)', padding: '4px 0' }}>
            Nessuna voce di costo. Premi "+ Aggiungi costo" per registrare spese.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {vociCosto.map((voce, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                <div style={{ flex: 2 }}>
                  {i === 0 && <label style={labelStyle}>Descrizione</label>}
                  <input placeholder="es. Raccorderia" value={voce.descrizione} onChange={e => updateVoce(i, 'descrizione', e.target.value)} style={inputStyle} />
                </div>
                <div style={{ flex: 1 }}>
                  {i === 0 && <label style={labelStyle}>Importo €</label>}
                  <input type="number" step="0.01" placeholder="0.00" value={voce.importo} onChange={e => updateVoce(i, 'importo', e.target.value)} style={inputStyle} />
                </div>
                <div style={{ flex: 1 }}>
                  {i === 0 && <label style={labelStyle}>Categoria</label>}
                  <select value={voce.categoria} onChange={e => updateVoce(i, 'categoria', e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
                    <option value="">—</option>
                    {CATEGORIE.map(c => <option key={c} value={c.toLowerCase()}>{c}</option>)}
                  </select>
                </div>
                <button onClick={() => removeVoce(i)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--ds-danger)', fontSize: 18, padding: '6px', minHeight: 36 }}>×</button>
              </div>
            ))}
          </div>
        )}
        {vociCosto.length > 0 && (
          <div style={{ fontSize: 11, color: 'var(--ds-muted)', marginTop: 6, fontStyle: 'italic' }}>
            Le voci inserite verranno salvate automaticamente nelle Spese del cantiere.
          </div>
        )}
      </div>}

      {/* VOX-ORE-C — Presenze: ore per persona (split). Visibili e modificabili. */}
      <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--ds-border)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <label style={labelStyle}>👷 Presenze (ore per persona)</label>
          <button onClick={addPresenza} style={btnSmall}>+ Aggiungi persona</button>
        </div>
        {presenze.length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--ds-muted)', padding: '4px 0' }}>
            Nessuna presenza nominale. Le ore restano come totale del cantiere.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {presenze.map((p, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                <div style={{ flex: 2 }}>
                  {i === 0 && <label style={labelStyle}>Nome</label>}
                  <input placeholder="es. Riccardo" value={p.nome} onChange={e => updatePresenza(i, 'nome', e.target.value)} style={inputStyle} />
                </div>
                <div style={{ flex: 1 }}>
                  {i === 0 && <label style={labelStyle}>Ore</label>}
                  <input type="number" min={0} placeholder="0" value={p.ore} onChange={e => updatePresenza(i, 'ore', e.target.value)} style={inputStyle} />
                </div>
                <button onClick={() => removePresenza(i)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--ds-danger)', fontSize: 18, padding: '6px', minHeight: 36 }}>×</button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
        <button onClick={onCancel} style={btnCancel}>Annulla</button>
        <button onClick={handleSubmit} style={btnPrimary}>{isEditing ? 'Salva modifiche' : 'Crea report'}</button>
      </div>
    </div>
  );
}

function EmptyState({ icon, title, subtitle }) {
  return (
    <div style={{ padding: 40, textAlign: 'center' }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>{icon}</div>
      <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--ds-text)' }}>{title}</div>
      <div style={{ fontSize: 13, color: 'var(--ds-muted)', marginTop: 4 }}>{subtitle}</div>
    </div>
  );
}

const btnPrimary = { padding: '10px 20px', borderRadius: 6, border: 'none', background: 'var(--brand)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 500 };
const btnCancel = { padding: '8px 16px', borderRadius: 6, border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', color: 'var(--ds-muted)', cursor: 'pointer', fontSize: 12 };
const btnSmall = { padding: '4px 12px', borderRadius: 6, border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', color: 'var(--ds-text)', cursor: 'pointer', fontSize: 12 };
const linkBtn = { padding: '6px 14px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', color: 'var(--ds-muted)', cursor: 'pointer', fontSize: 12 };
const inputStyle = { padding: '8px 12px', borderRadius: 6, border: '1px solid var(--ds-border)', fontSize: 13, width: '100%', background: 'var(--ds-bg)', color: 'var(--ds-text)' };
const labelStyle = { fontSize: 11, color: 'var(--ds-muted)', display: 'block', marginBottom: 3, textTransform: 'uppercase' };
